/**
 * Physics World - Cannon-ES Wrapper
 *
 * Optimized for dice physics with:
 * - Fixed timestep updates
 * - Position interpolation for smooth rendering
 * - Sleep detection for dice settling
 * - Configurable physics parameters
 */

import * as CANNON from 'cannon-es';

export interface PhysicsConfig {
  gravity?: number;
  iterations?: number;
  tolerance?: number;
  // Sleep settings
  allowSleep?: boolean;
  sleepSpeedLimit?: number;
  sleepTimeLimit?: number;
}

export interface PhysicsBodyConfig {
  mass: number;
  shape: CANNON.Shape;
  position?: { x: number; y: number; z: number };
  quaternion?: { x: number; y: number; z: number; w: number };
  material?: CANNON.Material;
  linearDamping?: number;
  angularDamping?: number;
  fixedRotation?: boolean;
  collisionFilterGroup?: number;
  collisionFilterMask?: number;
}

// Collision groups
export const CollisionGroups = {
  NONE: 0,
  DICE: 1 << 0,
  TABLE: 1 << 1,
  WALL: 1 << 2,
  BILL: 1 << 3,  // Bills have their own group
  ALL: 0xffffffff,
} as const;

export class PhysicsWorld {
  readonly world: CANNON.World;

  // Materials
  readonly diceMaterial: CANNON.Material;
  readonly tableMaterial: CANNON.Material;
  readonly wallMaterial: CANNON.Material;
  readonly billMaterial: CANNON.Material;

  // Bodies for interpolation
  private bodies: Map<CANNON.Body, {
    previousPosition: CANNON.Vec3;
    previousQuaternion: CANNON.Quaternion;
    interpolatedPosition: CANNON.Vec3;
    interpolatedQuaternion: CANNON.Quaternion;
  }> = new Map();

  // Contact materials (reusable)
  private diceTableContact: CANNON.ContactMaterial;
  private diceWallContact: CANNON.ContactMaterial;
  private diceDiceContact: CANNON.ContactMaterial;
  private diceBillContact: CANNON.ContactMaterial;
  private billTableContact: CANNON.ContactMaterial;
  private billBillContact: CANNON.ContactMaterial;

  constructor(config: PhysicsConfig = {}) {
    // Create world
    this.world = new CANNON.World();
    this.world.gravity.set(0, -(config.gravity ?? 30), 0);

    // Solver settings (GSSolver)
    const solver = this.world.solver as CANNON.GSSolver;
    solver.iterations = config.iterations ?? 10;
    solver.tolerance = config.tolerance ?? 0.001;

    // Broadphase
    this.world.broadphase = new CANNON.SAPBroadphase(this.world);

    // Sleep settings
    this.world.allowSleep = config.allowSleep ?? true;
    this.world.defaultContactMaterial.contactEquationStiffness = 1e8;
    this.world.defaultContactMaterial.contactEquationRelaxation = 3;

    // Create materials
    this.diceMaterial = new CANNON.Material('dice');
    this.tableMaterial = new CANNON.Material('table');
    this.wallMaterial = new CANNON.Material('wall');
    this.billMaterial = new CANNON.Material('bill');

    // Dice-Table contact
    this.diceTableContact = new CANNON.ContactMaterial(
      this.diceMaterial,
      this.tableMaterial,
      {
        friction: 0.4,
        restitution: 0.15,  // Low bounce on table
      }
    );
    this.world.addContactMaterial(this.diceTableContact);

    // Dice-Wall contact
    this.diceWallContact = new CANNON.ContactMaterial(
      this.diceMaterial,
      this.wallMaterial,
      {
        friction: 0.3,
        restitution: 0.3,  // More bounce off walls
      }
    );
    this.world.addContactMaterial(this.diceWallContact);

    // Dice-Dice contact
    this.diceDiceContact = new CANNON.ContactMaterial(
      this.diceMaterial,
      this.diceMaterial,
      {
        friction: 0.3,
        restitution: 0.4,  // Moderate bounce between dice
      }
    );
    this.world.addContactMaterial(this.diceDiceContact);

    // Dice-Bill/Prop contact (for imported meshes)
    this.diceBillContact = new CANNON.ContactMaterial(
      this.diceMaterial,
      this.billMaterial,
      {
        friction: 0.5,      // Good friction to stop sliding
        restitution: 0.2,   // Slight bounce
        contactEquationStiffness: 1e8,
        contactEquationRelaxation: 3,
      }
    );
    this.world.addContactMaterial(this.diceBillContact);

    // Bill-Table contact (medium friction, slight bounce)
    this.billTableContact = new CANNON.ContactMaterial(
      this.billMaterial,
      this.tableMaterial,
      {
        friction: 0.5,      // Medium friction for sliding and settling
        restitution: 0.2,   // Slight bounce on table
        contactEquationStiffness: 1e8,  // High stiffness to prevent penetration
        contactEquationRelaxation: 3,
      }
    );
    this.world.addContactMaterial(this.billTableContact);

    // Bill-Bill contact (bills push each other apart when stacking)
    this.billBillContact = new CANNON.ContactMaterial(
      this.billMaterial,
      this.billMaterial,
      {
        friction: 0.3,      // Low friction for sliding over each other
        restitution: 0.1,   // Minimal bounce
        contactEquationStiffness: 1e7,  // Softer collision for smoother stacking
        contactEquationRelaxation: 4,
      }
    );
    this.world.addContactMaterial(this.billBillContact);
  }

  /**
   * Create and add a physics body
   */
  createBody(config: PhysicsBodyConfig): CANNON.Body {
    const body = new CANNON.Body({
      mass: config.mass,
      material: config.material,
      linearDamping: config.linearDamping ?? 0.2,
      angularDamping: config.angularDamping ?? 0.4,
      fixedRotation: config.fixedRotation ?? false,
      collisionFilterGroup: config.collisionFilterGroup ?? CollisionGroups.ALL,
      collisionFilterMask: config.collisionFilterMask ?? CollisionGroups.ALL,
      allowSleep: true,
      sleepSpeedLimit: 0.5,
      sleepTimeLimit: 0.5,
    });

    body.addShape(config.shape);

    if (config.position) {
      body.position.set(config.position.x, config.position.y, config.position.z);
    }

    if (config.quaternion) {
      body.quaternion.set(
        config.quaternion.x,
        config.quaternion.y,
        config.quaternion.z,
        config.quaternion.w
      );
    }

    this.world.addBody(body);

    // Setup interpolation tracking
    this.bodies.set(body, {
      previousPosition: body.position.clone(),
      previousQuaternion: body.quaternion.clone(),
      interpolatedPosition: body.position.clone(),
      interpolatedQuaternion: body.quaternion.clone(),
    });

    return body;
  }

  /**
   * Remove a body from the world
   */
  removeBody(body: CANNON.Body): void {
    this.world.removeBody(body);
    this.bodies.delete(body);
  }

  /**
   * Re-add a previously removed body to the world (pool reuse).
   * Resets state and sets up interpolation tracking.
   */
  reuseBody(body: CANNON.Body, position?: { x: number; y: number; z: number }): void {
    body.velocity.setZero();
    body.angularVelocity.setZero();
    body.force.setZero();
    body.torque.setZero();
    if (position) {
      body.position.set(position.x, position.y, position.z);
    }
    body.quaternion.set(0, 0, 0, 1);
    body.wakeUp();

    this.world.addBody(body);
    this.bodies.set(body, {
      previousPosition: body.position.clone(),
      previousQuaternion: body.quaternion.clone(),
      interpolatedPosition: body.position.clone(),
      interpolatedQuaternion: body.quaternion.clone(),
    });
  }

  /**
   * Store previous state before physics step (call before step)
   */
  storePreviousState(): void {
    for (const [body, state] of this.bodies) {
      // Sleeping bodies don't move between steps — their previous state is
      // already current (interpolate() keeps it fresh even across teleports
      // of slept bodies). Settled dice + parked bills skip the copies.
      if (body.sleepState === CANNON.Body.SLEEPING) continue;
      state.previousPosition.copy(body.position);
      state.previousQuaternion.copy(body.quaternion);
    }
  }

  /**
   * Fixed physics step (call at fixed intervals)
   */
  step(deltaTime: number): void {
    this.world.step(deltaTime);
  }

  /**
   * True if any die is currently in free flight: a DICE-group body that is
   * dynamic (mass > 0) and awake. Held dice (mass 0) and parked/settled dice
   * (asleep) are excluded — so this flips true the frame a die is released and
   * false the frame the last die settles, with no external bookkeeping.
   *
   * The GameLoop reads this every frame: while it's true we step the world ONE
   * fixed step per rendered frame instead of the wall-clock accumulator, so the
   * step COUNT from throw to rest is identical on every client regardless of
   * frame rate. That is what makes the 3-dice roll bit-identical across devices
   * (the cause of spectators seeing a different roll that snapped on stop was
   * each device running a different number of steps for the same throw).
   */
  anyDiceInFlight(): boolean {
    for (const body of this.bodies.keys()) {
      if (
        body.collisionFilterGroup === CollisionGroups.DICE &&
        body.mass > 0 &&
        body.sleepState !== CANNON.Body.SLEEPING
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Emit a structured dump of every body in the world — for client/server
   * parity checks. Disabled by default; set window.__SDL_PHYSICS_DUMP = true
   * in the console to enable when chasing a determinism bug.
   */
  dumpBodies(_label: string): void {
    // Client/server physics-parity dump — diagnostic logging stripped for prod.
  }

  /**
   * Interpolate positions for smooth rendering (call before render)
   * @param alpha Interpolation factor (0-1, from accumulator/fixedDeltaTime)
   */
  interpolate(alpha: number): void {
    for (const [body, state] of this.bodies) {
      // Sleeping bodies don't move — interpolation is an identity lerp, and
      // the per-body slerp is the expensive part of this loop. Snap once if a
      // slept body was teleported (pose corrections set position/quaternion
      // directly while asleep), refreshing previous* too so a later wake-up
      // lerps from the right pose; otherwise skip entirely.
      if (body.sleepState === CANNON.Body.SLEEPING) {
        const ip = state.interpolatedPosition;
        const iq = state.interpolatedQuaternion;
        const bp = body.position;
        const bq = body.quaternion;
        if (
          ip.x !== bp.x || ip.y !== bp.y || ip.z !== bp.z ||
          iq.x !== bq.x || iq.y !== bq.y || iq.z !== bq.z || iq.w !== bq.w
        ) {
          ip.copy(bp);
          iq.copy(bq);
          state.previousPosition.copy(bp);
          state.previousQuaternion.copy(bq);
        }
        continue;
      }

      // Lerp position
      state.interpolatedPosition.x =
        state.previousPosition.x + (body.position.x - state.previousPosition.x) * alpha;
      state.interpolatedPosition.y =
        state.previousPosition.y + (body.position.y - state.previousPosition.y) * alpha;
      state.interpolatedPosition.z =
        state.previousPosition.z + (body.position.z - state.previousPosition.z) * alpha;

      // Slerp quaternion (from previous to current by alpha)
      state.previousQuaternion.slerp(body.quaternion, alpha, state.interpolatedQuaternion);
    }
  }

  /**
   * Get interpolated position for smooth rendering
   */
  getInterpolatedPosition(body: CANNON.Body): CANNON.Vec3 | null {
    return this.bodies.get(body)?.interpolatedPosition ?? null;
  }

  /**
   * Get interpolated quaternion for smooth rendering
   */
  getInterpolatedQuaternion(body: CANNON.Body): CANNON.Quaternion | null {
    return this.bodies.get(body)?.interpolatedQuaternion ?? null;
  }

  /**
   * Check if a body is sleeping (at rest)
   */
  isSleeping(body: CANNON.Body): boolean {
    return body.sleepState === CANNON.Body.SLEEPING;
  }

  /**
   * Check if all tracked bodies are sleeping
   */
  areAllBodiesSleeping(): boolean {
    for (const body of this.bodies.keys()) {
      if (body.mass > 0 && !this.isSleeping(body)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Get velocity magnitude
   */
  getSpeed(body: CANNON.Body): number {
    return body.velocity.length();
  }

  /**
   * Wake up a body
   */
  wakeUp(body: CANNON.Body): void {
    body.wakeUp();
  }

  /**
   * Apply impulse to a body
   */
  applyImpulse(
    body: CANNON.Body,
    impulse: { x: number; y: number; z: number },
    point?: { x: number; y: number; z: number }
  ): void {
    const impulseVec = new CANNON.Vec3(impulse.x, impulse.y, impulse.z);
    const pointVec = point
      ? new CANNON.Vec3(point.x, point.y, point.z)
      : body.position;

    body.applyImpulse(impulseVec, pointVec);
  }

  /**
   * Set body position
   */
  setPosition(body: CANNON.Body, pos: { x: number; y: number; z: number }): void {
    body.position.set(pos.x, pos.y, pos.z);
    body.previousPosition.copy(body.position);

    const state = this.bodies.get(body);
    if (state) {
      state.previousPosition.copy(body.position);
      state.interpolatedPosition.copy(body.position);
    }
  }

  /**
   * Set body quaternion
   */
  setQuaternion(body: CANNON.Body, quat: { x: number; y: number; z: number; w: number }): void {
    body.quaternion.set(quat.x, quat.y, quat.z, quat.w);
    body.previousQuaternion.copy(body.quaternion);

    const state = this.bodies.get(body);
    if (state) {
      state.previousQuaternion.copy(body.quaternion);
      state.interpolatedQuaternion.copy(body.quaternion);
    }
  }

  /**
   * Reset body state
   */
  resetBody(body: CANNON.Body): void {
    body.velocity.setZero();
    body.angularVelocity.setZero();
    body.force.setZero();
    body.torque.setZero();
    body.wakeUp();
  }

  /**
   * Update physics parameters
   */
  setGravity(gravity: number): void {
    this.world.gravity.set(0, -gravity, 0);
  }

  setDiceTableBounce(restitution: number): void {
    this.diceTableContact.restitution = restitution;
  }

  setDiceWallBounce(restitution: number): void {
    this.diceWallContact.restitution = restitution;
  }

  setDiceDiceBounce(restitution: number): void {
    this.diceDiceContact.restitution = restitution;
  }

  /**
   * Get the Cannon.js world instance
   */
  getWorld(): CANNON.World {
    return this.world;
  }

  /**
   * Get the bill material
   */
  getBillMaterial(): CANNON.Material {
    return this.billMaterial;
  }

  /**
   * Dispose of all resources
   */
  dispose(): void {
    // Remove all bodies
    for (const body of this.bodies.keys()) {
      this.world.removeBody(body);
    }
    this.bodies.clear();
  }
}
